import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// "Connect X" for organic X (Twitter) analytics + the feed import
// (server/xConnect.js). Same component on both surfaces via the scope prop,
// like TikTokConnectCard. Tokens are OAuth-minted and auto-refreshed
// server-side — nothing to paste. Renders nothing until the platform X app
// (client id/secret) is configured.
export default function XConnectCard({ entityId, scope = 'my' }) {
  const get = () => (scope === 'admin-client' ? api.adminXConnect(entityId) : api.myXConnect(entityId));
  const startFn = () => (scope === 'admin-client' ? api.adminXConnectStart(entityId, retPath()) : api.myXConnectStart(entityId, retPath()));
  const discFn = () => (scope === 'admin-client' ? api.adminXConnectDisconnect(entityId) : api.myXConnectDisconnect(entityId));
  const retPath = () => window.location.pathname + window.location.search;

  const [v, setV] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [test, setTest] = useState(null); // hooks must ALL precede the early return below
  useEffect(() => { get().then(setV).catch(() => setV(null)); }, [entityId, scope]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!v?.appConfigured) return null;

  const connect = async () => {
    setBusy(true); setErr('');
    try { const r = await startFn(); window.location.href = r.url; }
    catch (e) { setErr(e.message); setBusy(false); }
  };
  const act = async (fn) => {
    setBusy(true); setErr('');
    try { setV(await fn()); } catch (e) { setErr(e.message); }
    setBusy(false);
  };
  // One-tap pull: syncs this client's X (and other connected socials) now, so
  // first data shows without waiting for the 4am daily run.
  const runSync = async () => {
    setTest('…');
    try {
      const r = await api.syncSocial(entityId);
      const t = r?.results?.x;
      setTest(t ? (t.ok ? '✓ Synced — X stats are on the Social page' : `✗ Sync failed: ${t.error || 'unknown'}`) : '✗ X not in the sync result — is it connected?');
    } catch (e) { setTest(`⚠ ${e.message}`); }
  };

  return (
    <div style={card}>
      <div style={title}>𝕏 X — connect for analytics</div>
      <p style={sub}>
        One login shows this client's own X <b>followers, likes and per-post stats</b> on the Social page,
        refreshed daily — and lets recent X posts be reposted onto the Howler feed. Access renews itself
        in the background — no tokens to paste.
      </p>
      {err && <p style={{ ...sub, color: 'var(--danger, #dc2626)' }}>{err}</p>}

      {v.connected ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 220px', minWidth: 0, fontSize: 13 }}>
            <b>Connected{v.connectedAs ? ` as ${v.connectedAs}` : ''}{v.username ? ` (@${v.username})` : ''}</b>
            {v.refreshError ? (
              <div style={{ fontSize: 12, marginTop: 2, color: 'var(--danger, #dc2626)' }}>⚠ Auto-renew failed ({v.refreshError}) — reconnect below</div>
            ) : (
              <div style={{ fontSize: 12, marginTop: 2, color: 'var(--muted)' }}>Self-renews in the background</div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button style={v.needsReconnect ? btn : ghostBtn} disabled={busy} onClick={connect}>{v.needsReconnect ? 'Reconnect' : 'Reconnect / switch'}</button>
            <button style={dangerBtn} disabled={busy} onClick={() => window.confirm('Disconnect X? Daily analytics stop until reconnected.') && act(discFn)}>Disconnect</button>
          </div>
        </div>
      ) : (
        <button style={{ ...btn, background: '#000', borderColor: '#000' }} disabled={busy} onClick={connect}>
          {busy ? 'Opening…' : 'Connect X'}
        </button>
      )}

      {v.connected && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px dashed var(--hairline)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button style={ghostBtn} disabled={test === '…'} onClick={runSync}>{test === '…' ? 'Syncing…' : 'Sync stats now'}</button>
            <span style={{ fontSize: 12, color: 'var(--muted)', flex: '1 1 200px' }}>Pulls today's X numbers immediately instead of waiting for the nightly run.</span>
          </div>
          {test && test !== '…' && (
            <div style={{ fontSize: 12.5, marginTop: 8, color: test.startsWith('✓') ? 'var(--success, #10b981)' : 'var(--danger, #dc2626)' }}>{test}</div>
          )}
        </div>
      )}
    </div>
  );
}

const card = { background: 'var(--card)', border: '1px solid var(--hairline)', borderRadius: 12, padding: 16, marginTop: 16 };
const title = { fontSize: 14, fontWeight: 700, marginBottom: 6 };
const sub = { fontSize: 13, color: 'var(--muted)', margin: '0 0 12px' };
const btn = { border: '1px solid var(--hairline)', background: 'var(--brand)', color: '#fff', borderRadius: 980, padding: '9px 16px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', minHeight: 40 };
const ghostBtn = { ...btn, background: 'transparent', color: 'inherit', display: 'flex', alignItems: 'center', gap: 6 };
const dangerBtn = { ...ghostBtn, color: 'var(--danger, #dc2626)', flexShrink: 0 };
